
import { AlertTriangle, Phone, ExternalLink } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface ClinicalDisclaimerPanelProps {
  variant?: "full" | "compact" | "emergency" | "brief";
  showEmergencyContacts?: boolean;
  className?: string;
}

const ClinicalDisclaimerPanel = ({ 
  variant = "full", 
  showEmergencyContacts = false,
  className = "" 
}: ClinicalDisclaimerPanelProps) => {
  const handleEmergencyCall = () => {
    window.location.href = "tel:999";
  };

  if (variant === "emergency") {
    return (
      <Card className={`p-6 bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200 shadow-lg ${className}`}>
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center">
            <div className="bg-red-100 p-3 rounded-full">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-red-900">Emergency Symptoms?</h3>
          <p className="text-red-800 text-lg max-w-2xl mx-auto">
            If you're experiencing chest pain, difficulty breathing, severe bleeding, loss of consciousness, 
            or other life-threatening symptoms, call emergency services immediately.
          </p>
          <Button 
            onClick={handleEmergencyCall}
            className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 text-xl font-bold"
          >
            <Phone className="mr-3 h-6 w-6" />
            Call 999 Now
          </Button>
        </div>
      </Card>
    );
  }

  if (variant === "brief") {
    return (
      <div className={`p-3 bg-blue-50 border border-blue-200 rounded-lg ${className}`}>
        <div className="flex items-start space-x-2">
          <AlertTriangle className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs text-blue-800">
              <strong>Medical Disclaimer:</strong> AmIOK provides clinical decision support, not medical diagnosis. 
              Always consult healthcare professionals for medical concerns.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (variant === "compact") {
    return (
      <div className={`p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded ${className}`}>
        <div className="flex items-start space-x-3">
          <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-yellow-800">
              <strong>Medical Disclaimer:</strong> AmIOK is a clinical decision support tool, not a substitute for professional medical advice. 
              Always consult with qualified healthcare professionals for medical concerns.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card className={`p-8 bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200 shadow-lg ${className}`}>
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <div className="bg-amber-100 p-3 rounded-full">
            <AlertTriangle className="h-6 w-6 text-amber-600" />
          </div>
          <h3 className="text-2xl font-bold text-amber-900">Important Medical Disclaimer</h3>
        </div>
        
        <div className="space-y-4 text-amber-800">
          <p className="text-lg leading-relaxed">
            <strong>AmIOK is a clinical decision support tool designed to provide evidence-based health guidance.</strong> 
            It is NOT a substitute for professional medical advice, diagnosis, or treatment.
          </p>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-white/60 rounded-lg">
              <h4 className="font-semibold text-amber-900 mb-2">What AmIOK Provides:</h4>
              <ul className="text-sm space-y-1">
                <li>• Evidence-based health assessments</li>
                <li>• Clinical decision support guidance</li>
                <li>• Risk level categorization (AmIOK Score)</li>
                <li>• Peer-reviewed research references</li>
                <li>• Diagnostic analysis and recommendations</li>
              </ul>
            </div>
            
            <div className="p-4 bg-white/60 rounded-lg">
              <h4 className="font-semibold text-amber-900 mb-2">What AmIOK Does NOT Provide:</h4>
              <ul className="text-sm space-y-1">
                <li>• Definitive medical diagnosis</li>
                <li>• Emergency medical services</li>
                <li>• Prescription recommendations</li>
                <li>• Replacement for doctor consultations</li>
                <li>• Treatment protocols</li>
              </ul>
            </div>
          </div>
          
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 font-medium">
              Always seek immediate medical attention for emergencies. If you have concerns about your health, 
              consult with qualified healthcare professionals who can provide personalized medical advice based on 
              your complete medical history and physical examination.
            </p>
          </div>
        </div>

        {showEmergencyContacts && (
          <div className="border-t border-amber-200 pt-6">
            <h4 className="font-semibold text-amber-900 mb-4">Emergency Contacts (UK)</h4>
            <div className="grid md:grid-cols-3 gap-4">
              <a
                href="tel:999"
                className="p-3 bg-white/60 rounded-lg text-center block hover:bg-white/80 transition-colors cursor-pointer"
              >
                <Phone className="h-5 w-5 text-red-600 mx-auto mb-1" />
                <div className="font-bold text-red-600">999</div>
                <div className="text-xs text-gray-600">Emergency Services</div>
              </a>
              <a
                href="tel:111"
                className="p-3 bg-white/60 rounded-lg text-center block hover:bg-white/80 transition-colors cursor-pointer"
              >
                <Phone className="h-5 w-5 text-blue-600 mx-auto mb-1" />
                <div className="font-bold text-blue-600">NHS 111</div>
                <div className="text-xs text-gray-600">Non-Emergency Health</div>
              </a>
              <a
                href="https://www.nhs.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-white/60 rounded-lg text-center block hover:bg-white/80 transition-colors cursor-pointer"
              >
                <ExternalLink className="h-5 w-5 text-green-600 mx-auto mb-1" />
                <div className="font-bold text-green-600">NHS Online</div>
                <div className="text-xs text-gray-600">NHS.UK Services</div>
              </a>
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-center space-x-4 pt-4 border-t border-amber-200">
          <Badge variant="outline" className="bg-amber-100 text-amber-800">
            Clinical Decision Support Tool
          </Badge>
          <Badge variant="outline" className="bg-blue-100 text-blue-800">
            Evidence-Based Guidance
          </Badge>
          <Badge variant="outline" className="bg-purple-100 text-purple-800">
            Research-Integrated Data
          </Badge>
        </div>
      </div>
    </Card>
  );
};

export default ClinicalDisclaimerPanel;
